import React from 'react';
import { motion } from 'framer-motion';
import { BrainIcon, ZapOffIcon, ShoppingCartIcon, BatteryLowIcon, AlertTriangleIcon } from 'lucide-react';
export function WhySection() {
  const decisions = ['¿Qué desayunan los niños?', '¿Qué les pongo hoy?', '¿A qué hora los llevo?', '¿Respondo este mensaje o el otro?', '¿Compro hoy o espero?'];
  const emergencyModeEffects = [{
    icon: ZapOffIcon,
    title: 'Bloquea la creatividad',
    description: 'No es momento de tener ideas, es momento de sobrevivir'
  }, {
    icon: ShoppingCartIcon,
    title: 'Acelera las decisiones impulsivas',
    description: 'Por eso terminas comprando cosas que no necesitas o respondiendo mensajes desde el miedo'
  }, {
    icon: BatteryLowIcon,
    title: 'Consume toda tu energía',
    description: 'Por eso llegas al final del día agotada aunque físicamente no hayas hecho nada extenuante'
  }];
  return <section className="w-full bg-[#FFF8F0] px-4 md:px-6 py-6 md:py-24 text-center" data-id="element-427">
      <div className="max-w-4xl mx-auto" data-id="element-428">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-429">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl lg:text-5xl font-bold text-[#2D2D2D] mb-5 md:mb-14" data-id="element-430">
            Aquí está lo que{' '}
            <span className="text-[#E8734A]" data-id="element-431">nadie te dijo:</span>
          </h2>

          {/* Decision fatigue */}
          <div className="max-w-3xl mx-auto mb-5 md:mb-14" data-id="element-432">
            <div className="bg-[#2D2D2D] rounded-2xl md:rounded-3xl p-5 md:p-14 shadow-2xl relative overflow-hidden" data-id="element-433">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#E8734A] rounded-full opacity-10 blur-3xl" data-id="element-434" />

              <p className="text-sm uppercase tracking-[0.2em] text-white/40 font-bold mb-3 md:mb-4" data-id="element-435">
                Antes de sentarte a trabajar
              </p>

              <div className="flex items-baseline justify-center gap-2 md:gap-3 mb-3 md:mb-6" data-id="element-436">
                <span className="text-5xl md:text-8xl font-black text-[#D4A853]" data-id="element-437">
                  80–200
                </span>
                <span className="text-xl text-white/70 font-medium" data-id="element-438">
                  decisiones
                </span>
              </div>

              <p className="text-base md:text-lg text-white/80 font-medium mb-4 md:mb-8" data-id="element-439">
                ya las tomaste hoy sin darte cuenta:
              </p>

              <div className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-440">
                {decisions.map((decision, index) => <motion.span key={index} initial={{
                opacity: 0,
                scale: 0.8
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: index * 0.08
              }} className="bg-white/10 text-white/80 text-sm font-medium px-5 py-2.5 rounded-full border border-white/10" data-id="element-441">
                    {decision}
                  </motion.span>)}
              </div>
            </div>
          </div>

          {/* Muscle metaphor */}
          <div className="grid md:grid-cols-2 gap-3 md:gap-6 max-w-3xl mx-auto mb-5 md:mb-14" data-id="element-442">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl p-5 md:p-8 shadow-lg border border-[#F5E6DC] text-center" data-id="element-443">
              <div className="w-16 h-16 bg-[#F5E6DC] rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-5" data-id="element-444">
                <BrainIcon className="w-8 h-8 text-[#2D2D2D] opacity-50" data-id="element-445" />
              </div>
              <p className="text-lg text-[#2D2D2D] leading-relaxed" data-id="element-446">
                Tu músculo de decidir ya está{' '}
                <span className="font-bold text-[#2D2D2D]" data-id="element-447">agotado</span> cuando
                llega el momento de trabajar.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.15
          }} className="bg-white rounded-2xl p-5 md:p-8 shadow-lg border-2 border-[#E8734A]/20 text-center" data-id="element-448">
              <div className="w-16 h-16 bg-[#E8734A]/10 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-5" data-id="element-449">
                <BrainIcon className="w-8 h-8 text-[#E8734A]" data-id="element-450" />
              </div>
              <p className="text-lg text-[#2D2D2D] leading-relaxed" data-id="element-451">
                Y un músculo agotado no es un músculo perezoso. Es un músculo
                que{' '}
                <span className="font-bold text-[#E8734A]" data-id="element-452">
                  necesita recuperarse.
                </span>
              </p>
            </motion.div>
          </div>

          {/* Tiger vs bill */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="max-w-2xl mx-auto mb-5 md:mb-14" data-id="element-453">
            <div className="bg-white rounded-2xl p-5 md:p-10 shadow-lg border border-[#F5E6DC]" data-id="element-454">
              <div className="w-14 h-14 bg-[#D4A853]/15 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6" data-id="element-455">
                <AlertTriangleIcon className="w-7 h-7 text-[#D4A853]" data-id="element-456" />
              </div>
              <p className="text-lg text-[#2D2D2D] leading-relaxed mb-3 md:mb-4" data-id="element-457">
                Tu sistema de alertas no distingue entre un{' '}
                <span className="font-bold" data-id="element-458">tigre de verdad</span> y una{' '}
                <span className="font-bold" data-id="element-459">factura sin pagar</span>.
              </p>
              <p className="text-xl font-bold text-[#2D2D2D]" data-id="element-460">
                Para tu cuerpo, ambos son emergencias.
              </p>
            </div>
          </motion.div>

          {/* Emergency mode effects */}
          <div className="max-w-3xl mx-auto mb-5 md:mb-14" data-id="element-461">
            <p className="text-lg md:text-xl font-bold text-[#E8734A] mb-4 md:mb-8" data-id="element-462">
              Y cuando tu cuerpo está en "Modo Emergencia":
            </p>

            <div className="grid md:grid-cols-3 gap-3 md:gap-5" data-id="element-463">
              {emergencyModeEffects.map((effect, index) => {
              const Icon = effect.icon;
              return <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.12
              }} className="bg-white rounded-2xl p-4 md:p-7 shadow-lg border border-[#F5E6DC] text-center" data-id="element-464">
                    <div className="w-12 h-12 bg-[#E8734A]/10 rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-5" data-id="element-465">
                      <Icon className="w-6 h-6 text-[#E8734A]" data-id="element-466" />
                    </div>
                    <p className="text-base font-bold text-[#2D2D2D] mb-2" data-id="element-467">
                      {effect.title}
                    </p>
                    <p className="text-sm text-[#2D2D2D] opacity-70 leading-relaxed" data-id="element-468">
                      {effect.description}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Conclusion banner */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-[#E8734A] text-white rounded-2xl md:rounded-3xl p-6 md:p-12 max-w-2xl mx-auto shadow-2xl relative overflow-hidden" data-id="element-469">
            <div className="absolute -top-10 -left-10 w-32 h-32 bg-white rounded-full opacity-5 blur-2xl" data-id="element-470" />
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-[#D4A853] rounded-full opacity-10 blur-2xl" data-id="element-471" />

            <div className="relative z-10" data-id="element-472">
              <p className="text-2xl md:text-4xl font-['Playfair_Display'] font-bold mb-2 md:mb-3" data-id="element-473">
                No es falta de disciplina.
              </p>
              <p className="text-2xl md:text-4xl font-['Playfair_Display'] font-bold mb-3 md:mb-6" data-id="element-474">
                Es biología.
              </p>
              <div className="w-16 h-0.5 bg-white/40 mx-auto mb-4 md:mb-6" data-id="element-475" />
              <p className="text-xl text-[#FFF8F0] font-medium" data-id="element-476">
                Y la biología tiene solución.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}